import { useAuth0 } from '@auth0/auth0-react';
import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Coin from './Coin';
import './LK.css';

const timeZones = [
  { offset: -12, name: "UTC-12 (Бейкер)", zone: "Etc/GMT+12" },
  { offset: -11, name: "UTC-11 (Пагопагo)", zone: "Pacific/Pago_Pago" },
  { offset: -10, name: "UTC-10 (Гонолулу)", zone: "Pacific/Honolulu" },
  { offset: -9, name: "UTC-9 (Анкоридж)", zone: "America/Anchorage" },
  { offset: -8, name: "UTC-8 (Лос-Анджелес)", zone: "America/Los_Angeles" },
  { offset: -7, name: "UTC-7 (Денвер)", zone: "America/Denver" },
  { offset: -6, name: "UTC-6 (Чикаго)", zone: "America/Chicago" },
  { offset: -5, name: "UTC-5 (Нью-Йорк)", zone: "America/New_York" },
  { offset: -4, name: "UTC-4 (Каракас)", zone: "America/Caracas" },
  { offset: -3, name: "UTC-3 (Сан-Паулу)", zone: "America/Sao_Paulo" },
  { offset: -2, name: "UTC-2 (Южная Георгия)", zone: "Atlantic/South_Georgia" },
  { offset: -1, name: "UTC-1 (Рейкьявик)", zone: "Atlantic/Reykjavik" },
  { offset: 0, name: "UTC±0 (Лондон)", zone: "Europe/London" },
  { offset: 1, name: "UTC+1 (Берлин)", zone: "Europe/Berlin" },
  { offset: 2, name: "UTC+2 (Киев)", zone: "Europe/Kiev" },
  { offset: 3, name: "UTC+3 (Москва)", zone: "Europe/Moscow" },
  { offset: 4, name: "UTC+4 (Самара)", zone: "Europe/Samara" },
  { offset: 5, name: "UTC+5 (Екатеринбург)", zone: "Asia/Yekaterinburg" },
  { offset: 6, name: "UTC+6 (Омск)", zone: "Asia/Omsk" },
  { offset: 7, name: "UTC+7 (Новосибирск)", zone: "Asia/Novosibirsk" },
  { offset: 8, name: "UTC+8 (Иркутск)", zone: "Asia/Irkutsk" },
  { offset: 9, name: "UTC+9 (Якутск)", zone: "Asia/Yakutsk" },
  { offset: 10, name: "UTC+10 (Владивосток)", zone: "Asia/Vladivostok" },
  { offset: 11, name: "UTC+11 (Магадан)", zone: "Asia/Magadan" },
  { offset: 12, name: "UTC+12 (Анадырь)", zone: "Asia/Anadyr" },
];

const LK = () => {
  const { user, isAuthenticated, logout, loginWithRedirect, isLoading } = useAuth0();
  const [selectedTimeZone, setSelectedTimeZone] = useState(() => {
    const savedZone = localStorage.getItem('userTimeZone');
    return savedZone
      ? timeZones.find((tz) => tz.zone === savedZone)?.zone || Intl.DateTimeFormat().resolvedOptions().timeZone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  const [timeToMidnight, setTimeToMidnight] = useState('');
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const isAdmin = user?.['https://sergey-mavrodi.com/roles'] === 'admin';

  // Проверка флага функциональности
  useEffect(() => {
    const checkFeature = async () => {
      try {
        const response = await fetch('/api/features/favoritesEnabled');
        const data = await response.json();
        setIsFeatureEnabled(data.enabled);
      } catch (error) {
        console.error('Ошибка при проверке флага:', error);
      }
    };

    checkFeature();
  }, []);

  useEffect(() => {
    localStorage.setItem('userTimeZone', selectedTimeZone);
  }, [selectedTimeZone]);

  useEffect(() => {
    const updateTimer = () => {
      const now = DateTime.now().setZone(selectedTimeZone);
      if (now.hour === 0 && now.minute >= 0 && now.minute <= 15) {
        setTimeToMidnight('Время общения с Cистемой! (00:00–00:15)');
      } else {
        const midnight = now.plus({ days: now.hour >= 0 && (now.hour !== 0 || now.minute > 15) ? 1 : 0 }).startOf('day');
        const diff = midnight.diff(now, ['hours', 'minutes', 'seconds']).toObject();
        setTimeToMidnight(
          `До полуночи осталось: ${Math.floor(diff.hours || 0)}ч ${Math.floor(diff.minutes || 0)}м ${Math.floor(diff.seconds || 0)}с`
        );
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [selectedTimeZone]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleLogin = () => {
    loginWithRedirect();
  };

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  return (
    <div className="breadcrumbs">
      <div className="breadcrumb">
        <div id="breadcrumb"></div>
      </div>
      <div className="content">
        <div className="content_white_cloud">
          <div className="cwc_top"></div>
          <div className="cwc_mid">
            <div className="content">
                <br/>
                <h1>ЛИЧНЫЙ КАБИНЕТ</h1>
                <hr/>
              <div className="lk-content">

                {isAuthenticated ? (
                  <div>
                    <h2>Мы приветствуем Вас, {user?.username || user?.nickname || user?.name || user?.email || 'Пользователь'}!</h2>

                    <div className="timezone-section">
                      <label htmlFor="timezone-select" className="timezone-label">
                        Ваш часовой пояс:
                      </label>
                      <select
                        id="timezone-select"
                        value={selectedTimeZone}
                        onChange={(e) => setSelectedTimeZone(e.target.value)}
                        className="timezone-select"
                      >
                        {timeZones.map((tz) => (
                          <option key={tz.zone} value={tz.zone}>
                            {tz.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="timer">{timeToMidnight}</div>
                    {/* <hr /> */}

                    {(isAdmin || isFeatureEnabled) && (
                      <div className="favorites-section">
                        <Link to="/lk/favoriten">
                          <h2>ИЗБРАННОЕ</h2>
                        </Link>
                      </div>
                    )}
                    <hr />

                    <div className="coin-description">
                      ПОЗНАЙТЕ СВОЮ УДАЧУ<br />
                      Надо бросить сто раз монету. Результат первого броска – орел или решка – Ваша удача. Ее и надо считать. Орел первый раз выпал – значит, орла считать. Решка – значит, решку. Превышение над среднестатистическими 50% – и есть Ваша нынешняя удачливость, степень Вашего везения.
                    </div>
                    <Coin />
                    <br />
                    <hr />

                    <div className="warning">
                      ВНИМАНИЕ!!!<br />
                      Запрещено показывать опыт с монетой.<br />
                      Запрещено желать зла всей организации в целом или отдельным ее членам.
                    </div>

                    <button onClick={handleLogout} className="logout-button">
                      ВЫХОД
                    </button>
                  </div>
                ) : (
                  <div>
                    <h2>Канал общения со Вселенной открыт!</h2>
                    <button onClick={handleLogin} className="login-button">
                      ВХОД
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="cwc_bot"></div>
        </div>
      </div>
    </div>
  );
};

export default LK;